"use client";

import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
} from "@mui/material";
import {
  Close as CloseIcon, Save as SaveIcon,
  Build as BuildIcon, ReportProblem as AlertIcon,
  CalendarToday as CalIcon,
} from "@mui/icons-material";
import moment from "moment";
import { postData } from "../../../../../services/library";
import { usePlanta } from "../../../context/PlantaContext";
import { useReload } from "../../../context/ReloadContext";

function SectionHeader({ icon, label, color = "#3b82f6", bg = "#f8fafc" }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      px: 1.5, py: 0.9,
      bgcolor: bg,
      borderLeft: `3px solid ${color}`,
      borderRadius: "0 6px 6px 0",
    }}>
      <Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: "#94a3b8", textTransform: "uppercase" }}>
        {label}
      </Typography>
    </Box>
  );
}

export function CorrectiveMaintenanceForm({ open, onClose, equipment, setNotificacion }) {
  const { plantaSeleccionada } = usePlanta();
  const { triggerReload } = useReload();

  const [formData, setFormData] = useState({
    id_planta: Number(equipment.id_planta) || plantaSeleccionada,
    id_equipo: equipment.id,
    titulo: null, tarea: null, materiales: null,
    vencimiento: null, ejecuto: null, realizado: 0,
    costo_materiales: null, otros_costos: null,
    observaciones: null, observacionesOT: null,
    preparo: localStorage.getItem("user"),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const onPostCorrectivo = (e) => {
    try {
      postData(e, "/mp_correctivos/", formData, setNotificacion);
      triggerReload();
    } catch (error) {
      console.log(error);
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 0, borderBottom: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 1.5,
              bgcolor: "#fef2f2", border: "1px solid #fecaca",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <BuildIcon sx={{ fontSize: 17, color: "#dc2626" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b", lineHeight: 1.2 }}>
                Registrar Mantenimiento Correctivo
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: 11, mt: 0.2 }}>
                Registre la falla y las acciones a realizar
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <form action="POST" onSubmit={onPostCorrectivo}>
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={2}>

            {/* Equipo afectado */}
            <Grid item xs={12}>
              <Box sx={{
                display: "flex", alignItems: "center", gap: 2,
                p: 2, borderRadius: 1.5,
                bgcolor: "#fef2f2", border: "1px solid #fecaca",
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
                  bgcolor: "#fee2e2", border: "1px solid #fecaca",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <AlertIcon sx={{ fontSize: 20, color: "#dc2626" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#dc2626", textTransform: "uppercase", mb: 0.25 }}>
                    Equipo Afectado
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                    {equipment?.equipo}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>
                    ID: {equipment?.id} · Sección: {equipment?.sector}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <SectionHeader icon={<AlertIcon sx={{ fontSize: 14 }} />} label="Información de la Falla" color="#dc2626" bg="#fef2f2" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Título de la falla" name="titulo"
                value={formData.titulo ?? ""}
                onChange={handleChange}
                placeholder="Ej: Falla en sistema de refrigeración"
                required />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descripción de la tarea correctiva" name="tarea"
                value={formData.tarea ?? ""}
                onChange={handleChange}
                placeholder="Detalle la tarea a realizar para corregir la falla"
                required multiline rows={3} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Materiales necesarios" name="materiales"
                value={formData.materiales ?? ""}
                onChange={handleChange}
                placeholder="Liste los materiales requeridos para la reparación"
                multiline rows={2} />
            </Grid>

            <Grid item xs={12}>
              <SectionHeader icon={<CalIcon sx={{ fontSize: 14 }} />} label="Fecha Límite" />
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="Vencimiento del mantenimiento" name="vencimiento"
                type="date" InputLabelProps={{ shrink: true }}
                inputProps={{ min: moment(new Date()).format("YYYY-MM-DD") }}
                value={formData.vencimiento ?? ""}
                onChange={handleChange} required />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={onClose} color="inherit">Cancelar</Button>
          <Button type="submit" variant="contained" color="error" startIcon={<SaveIcon />}>
            Registrar Correctivo
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
